import {
  IExtendedISliceFields,
  IHasAlias,
  IHighlightColumn,
  IParsedAliasReplacements,
  IParsedChartData,
  IParsedChartDataCohortSample,
  IParsedChartDataDataTable,
  IParsedChartDataExpression,
  IParsedChartDataFunnel,
  IParsedChartDataPath,
  IParsedChartDataPrediction,
  IParsedChartDataRecord,
  IParsedChartDataRetention,
  IParsedChartDataSignificance,
  IParsedHighlight,
  ISliceFieldsFragment,
  RetentionTableType,
} from '../../components/Chart/interfaces';
import {
  FunnelModeKey,
  IUserAliasReplacement,
  Maybe,
  PathDirectionKey,
  PathModeKey,
  QueryType,
} from '../apollo/graphqlSchema';
import { assertCondition, isNotNil } from '../types';
import { isNil } from 'lodash';
import { DATA_KEY } from '../../components/Chart/constants/utils';
import { DataKey } from 'recharts/types/util/types';
import { UnreachableCaseError } from 'ts-essentials';

export const createEmptyChartData = (type: QueryType): IParsedChartData => {
  switch (type) {
    case QueryType.CohortSample:
      return {
        queryType: type,
        data: {
          subjectName: '',
          tables: [],
          summary: null,
        },
      } satisfies IParsedChartDataCohortSample;
    case QueryType.Prediction:
      return {
        queryType: type,
        data: {
          correlationKinds: [],
          events: [],
          selectedValue: '',
          correlationData: {},
        },
      } satisfies IParsedChartDataPrediction;

    case QueryType.Path:
      return {
        queryType: type,
        data: [],
        slices: [],
        mode: PathModeKey.Sankey,
        directionKey: PathDirectionKey.Forward,
        aliasReplacements: {},
        aliasReplacementsSnapshot: {},
        maxSeq: 1,
        eventsPerStep: 1,
        canCreateFunnel: { isDisabled: true, hint: 'Empty results' },
      } satisfies IParsedChartDataPath;
    case QueryType.Expression:
      return {
        queryType: type,
        data: [],
        linesMeta: {},
        statistics: [],
        slices: [],
        columns: [],
        measureControls: [],
        aliasReplacements: {},
        aliasReplacementsSnapshot: {},
        highlights: [],
        dataKeysOnMeasure: {},
        xAxis: null,
        dates: [],
        negativeValueDictionary: {},
      } satisfies IParsedChartDataExpression;
    case QueryType.DataTable:
      return {
        queryType: type,
        data: [],
        slices: [],
        columns: [],
        aliasReplacements: {},
        aliasReplacementsSnapshot: {},
        highlights: [],
        measureControls: [],
      } satisfies IParsedChartDataDataTable;
    case QueryType.Funnel:
      return {
        queryType: type,
        data: [],
        linesMeta: {},
        slices: [],
        columns: [],
        measureControls: [],
        aliasReplacements: {},
        aliasReplacementsSnapshot: {},
        xAxisKey: DATA_KEY,
        dates: [],
        highlights: [],
        xAxis: null,
        funnelModeSlices: [],
        mode: FunnelModeKey.Conversion,
        showInReportState: { isDisabled: false },
      } satisfies IParsedChartDataFunnel;
    case QueryType.Retention:
      return {
        queryType: type,
        data: [],
        slices: [],
        aliasReplacements: {},
        aliasReplacementsSnapshot: {},
        table: {
          type: RetentionTableType.Standard,
          rows: [],
          rowHeaders: [],
          columnHeaders: [],
          churnRange: { min: 0, max: 1 },
          retentionRange: { min: 0, max: 1 },
        },
        linesMeta: {},
      } satisfies IParsedChartDataRetention;
    case QueryType.Significance:
      return {
        queryType: type,
        data: null,
      } satisfies IParsedChartDataSignificance;
    default:
      throw new UnreachableCaseError(type);
  }
};

/**
 * Convert list of alias replacements into a map for easier lookup
 */
export const parseAliasReplacements = (
  aliasReplacements: Maybe<IUserAliasReplacement[]>,
): IParsedAliasReplacements => {
  if (isNil(aliasReplacements)) {
    return {};
  }
  return aliasReplacements.reduce<IParsedAliasReplacements>((acc, value) => {
    acc[value.aliasId] = value.replacement;
    return acc;
  }, {});
};

/**
 * Combine base and breakdown slices. Base can be optional (no breakdown overall)
 */
export const getAllSlices = <Slice extends ISliceFieldsFragment>(analysis: {
  data?: {
    base: Maybe<Slice>;
    slices: Slice[];
  };
}): Slice[] => {
  if (isNil(analysis.data)) {
    return [];
  }
  const {
    data: { slices, base: baseSlice },
  } = analysis;
  return [baseSlice, ...slices].filter(isNotNil);
};

/**
 * Helper function to extract a numeric value from chart data series data point. Provides type and runtime safety since
 * parsed chart data could contain strings (date axis) or booleans (isLive flag)
 *
 * Handles Rechart's DataKey which can be a function that extracts the value from the record
 */
export const getValueFromDataPoint = (
  dataRecord: IParsedChartDataRecord | undefined,
  key: DataKey<IParsedChartDataRecord> | number | undefined,
): number | undefined => {
  if (isNil(key) || isNil(dataRecord)) {
    return undefined;
  }
  let maybeNumber: unknown;
  if (typeof key === 'function') {
    maybeNumber = key(dataRecord);
  } else if (typeof key === 'string' || typeof key === 'number') {
    maybeNumber = dataRecord[key];
  } else {
    console.warn('key was not expected type (function, string or number)', {
      type: typeof key,
      key,
      dataRecord,
    });
    return undefined;
  }

  if (isNil(maybeNumber)) {
    return undefined;
  }

  let numberValue: number;
  if (typeof maybeNumber === 'string') {
    numberValue = parseFloat(maybeNumber);
  } else {
    if (typeof maybeNumber === 'number') {
      numberValue = maybeNumber;
    } else {
      console.warn('Neither string nor number found', {
        type: typeof maybeNumber,
        key,
        dataRecord,
      });
      return undefined;
    }
  }

  if (isFinite(numberValue) && !isNaN(numberValue)) {
    return numberValue;
  } else {
    console.warn('Special number value', {
      numberValue,
      maybeNumber,
      key,
      dataRecord,
    });
    return undefined;
  }
};

export const generateHighlights = (
  highlightColumns: IHighlightColumn[],
): IParsedHighlight[] =>
  highlightColumns.flatMap((column) =>
    (column?.reading?.highlights ?? []).map((highlight) => ({
      ...highlight,
      relatedLineName: column.key,
    })),
  );

export const toDefaultHasAlias = <T>(aliasable: T): T & IHasAlias => {
  return {
    ...aliasable,
    hasUserAlias: false,
  };
};

// XXX use this instead of toAliasableSlice for analyses which support comparison
export const toComparisonAliasableSlice = <
  T extends Pick<ISliceFieldsFragment, 'hint'>,
>(
  aliasable: T,
  isComparison: boolean,
): T & IHasAlias & IExtendedISliceFields => {
  return {
    ...aliasable,
    hasUserAlias: false,
    originalHint: aliasable.hint,
    isComparison,
  };
};

export const toAliasableSlice = <T extends Pick<ISliceFieldsFragment, 'hint'>>(
  aliasable: T,
): T & IHasAlias & IExtendedISliceFields =>
  toComparisonAliasableSlice(aliasable, false);

export const findByKey = <T extends { key: string }>(
  list: T[],
  key: string,
): T => {
  const result = list.find((x) => x.key === key);
  assertCondition(isNotNil(result), `Failed to find key ${key}`);
  return result;
};
